import {
  IEspActivityDto,
  IEspAssignmentDto,
  IEspOverviewDto,
  IEspPartnerDto,
  IEspRequestDto,
} from '../model/espDto'
import {
  IEspActivity,
  IEspOverview,
  IEspPartnerData,
  IEspRequest,
  IEspSponsorAssignment,
  IEspSponsorship,
} from '../model/types'

/** The service answers database date-times without a zone; read them as they are. */
const toTime = (value: string): number => new Date(value).getTime()

export const toEspRequest = (dto: IEspRequestDto): IEspRequest => ({
  id: String(dto.id),
  partnerId: dto.partnerId,
  type: dto.type,
  status: dto.status,
  sponsor: { name: dto.sponsorName, email: dto.sponsorEmail },
  requestedBy: dto.requestedByName || dto.requestedBy,
  accountInfo: {
    partnerId: dto.partnerId,
    partnerName: dto.accountInfo.partnerName ?? '',
    partnerCountry: dto.accountInfo.partnerCountry ?? '',
    accountClassification: dto.accountInfo.accountClassification ?? '',
    accountSegment: dto.accountInfo.accountSegment ?? '',
  },
  additionalInfo: {
    employeesCount: dto.additionalInfo.employeesCount,
    annualRevenueM: dto.additionalInfo.annualRevenueM,
    accountValue: dto.additionalInfo.accountValue ?? '',
    isBusinessGrowing: Boolean(dto.additionalInfo.isBusinessGrowing),
    accountContact: dto.additionalInfo.accountContact ?? '',
    missionAndVision: dto.additionalInfo.missionAndVision ?? '',
    keyChallenges: dto.additionalInfo.keyChallenges ?? '',
    productsAndSolutions: dto.additionalInfo.productsAndSolutions ?? '',
  },
  ...(dto.justification ? { justification: dto.justification } : {}),
  ...(dto.rejectionReason ? { rejectionReason: dto.rejectionReason } : {}),
  createdAt: toTime(dto.createdAt),
  ...(dto.resolvedAt ? { resolvedAt: toTime(dto.resolvedAt) } : {}),
})

const toEspAssignment = (dto: IEspAssignmentDto): IEspSponsorAssignment => ({
  id: String(dto.id),
  sponsor: { name: dto.sponsorName, email: dto.sponsorEmail },
  requestId: dto.requestId === null ? null : String(dto.requestId),
  assignedAt: toTime(dto.assignedAt),
  ...(dto.unassignedAt ? { unassignedAt: toTime(dto.unassignedAt) } : {}),
})

export const toEspActivity = (dto: IEspActivityDto): IEspActivity => ({
  id: String(dto.id),
  partnerId: dto.partnerId,
  date: dto.date,
  activityType: dto.activityType,
  meetingReason: dto.meetingReason,
  outcome: dto.outcome,
  attendees: dto.attendees,
  topics: dto.topics,
  notesAndDecisions: dto.notesAndDecisions,
  createdAt: toTime(dto.createdAt),
  updatedAt: toTime(dto.updatedAt),
})

/** One sponsorship per partner from its assignments; the current sponsor is the one never unassigned. */
export const toEspSponsorships = (assignments: IEspAssignmentDto[]): IEspSponsorship[] => {
  const byPartner = new Map<string, IEspAssignmentDto[]>()

  assignments.forEach((assignment) => {
    byPartner.set(assignment.partnerId, [
      ...(byPartner.get(assignment.partnerId) ?? []),
      assignment,
    ])
  })

  return [...byPartner].map(([partnerId, partnerAssignments]) => {
    const history = partnerAssignments
      .map(toEspAssignment)
      .sort((a, b) => b.assignedAt - a.assignedAt)

    return {
      partnerId,
      currentSponsor: history.find((assignment) => !assignment.unassignedAt)?.sponsor ?? null,
      history,
    }
  })
}

const byNewest = (a: { createdAt: number }, b: { createdAt: number }) => b.createdAt - a.createdAt

const byLatestDate = (a: IEspActivity, b: IEspActivity) =>
  b.date.localeCompare(a.date) || b.createdAt - a.createdAt

export const toEspPartnerData = (dto: IEspPartnerDto): IEspPartnerData => ({
  requests: dto.requests.map(toEspRequest).sort(byNewest),
  sponsorship: toEspSponsorships(dto.assignments)[0] ?? null,
  activities: dto.activities.map(toEspActivity).sort(byLatestDate),
})

export const toEspOverview = (dto: IEspOverviewDto): IEspOverview => ({
  requests: dto.requests.map(toEspRequest).sort(byNewest),
  sponsorships: toEspSponsorships(dto.assignments),
  activities: dto.activities.map(toEspActivity).sort(byLatestDate),
  partnerNames: Object.fromEntries(
    dto.partners.map((partner) => [partner.partnerId, partner.partnerName])
  ),
})

/** Groups a free-text sponsor: by email when there is one, by name otherwise. */
export const getEspSponsorKey = (sponsor: { name: string; email: string | null }): string =>
  (sponsor.email || sponsor.name).trim().toLowerCase()
